import { useQuery } from "@tanstack/react-query";
import { fetchCveBatch, NvdCveData } from "@/services/nvdApi";

/**
 * Hook to enrich a list of CVE IDs with real NVD data.
 * Only fetches when cveIds change. Results are cached by react-query + localStorage.
 */
export function useNvdEnrichment(cveIds: string[], enabled = true) {
  return useQuery({
    queryKey: ["nvd-enrichment", ...cveIds.slice(0, 20)], // limit key size
    queryFn: () => fetchCveBatch(cveIds),
    enabled: enabled && cveIds.length > 0,
    staleTime: 24 * 60 * 60 * 1000, // 24h
    gcTime: 48 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch NVD data for a single CVE (used in detail panels).
 */
export function useNvdCve(cveId: string | null) {
  return useQuery({
    queryKey: ["nvd-cve", cveId],
    queryFn: async () => {
      if (!cveId) return null;
      const map = await fetchCveBatch([cveId]);
      return map.get(cveId) || null;
    },
    enabled: !!cveId,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 48 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
